import { useQuery } from "@tanstack/react-query";

import { apiClient } from "@/api/apiClient";
import { getAssessmentByCaseNumberApiV1AssessmentCaseCaseNumberGet } from "@/api/generated/sdk.gen";
import type { AssessmentWithFieldStatusesSchema } from "@/api/generated/types.gen";

import { CLIENT_KEYS } from "@constants/clientKeys";

/**
 * Fetch assessment by case number
 * @param caseNumber - The case number to fetch assessment for
 * @param enabled - Whether the query should run
 * @param staleTime - How long the data stays fresh (optional)
 * @param retry - Number of retry attempts on failure (optional)
 * @returns Query result with assessment data including field statuses (AssessmentWithFieldStatusesSchema)
 */
export const useAssessmentByCaseNumber = ({
  caseNumber,
  enabled = true,
  staleTime = 5 * 60 * 1000, // 5 minutes
  retry = 1,
}: {
  caseNumber?: string;
  enabled?: boolean;
  staleTime?: number;
  retry?: number;
} = {}) => {
  return useQuery({
    queryKey: [CLIENT_KEYS.ASSESSMENTS, "case", caseNumber],
    queryFn: async (): Promise<AssessmentWithFieldStatusesSchema> => {
      if (!caseNumber) {
        throw new Error("Case number is required");
      }

      const response =
        await getAssessmentByCaseNumberApiV1AssessmentCaseCaseNumberGet({
          client: apiClient,
          path: { case_number: caseNumber },
        });

      // Check for errors in the response
      if ("error" in response && response.error) {
        throw new Error(
          `Failed to fetch assessment: ${response.error.detail || JSON.stringify(response.error)}`
        );
      }

      if (!response.data) {
        throw new Error(`Assessment not found for case: ${caseNumber}`);
      }

      return response.data;
    },
    enabled: enabled && !!caseNumber,
    staleTime,
    retry,
    // Show loading state for initial fetch
    placeholderData: undefined,
  });
};

/**
 * Hook to check if an assessment exists for a case
 * This is a lighter query that just checks existence without fetching full data
 */
export const useAssessmentExists = ({
  caseNumber,
  enabled = true,
}: {
  caseNumber?: string;
  enabled?: boolean;
} = {}) => {
  const { data, isLoading, error } = useAssessmentByCaseNumber({
    caseNumber,
    enabled,
    staleTime: 10 * 60 * 1000, // 10 minutes for existence check
  });

  return {
    exists: !!data && !error,
    isLoading,
    error,
  };
};
